/**
 * installed — is Karr running as the icon on a home screen, and keeping it
 * signed in there.
 *
 * ⚠ An iPhone home-screen app is a SEPARATE browser from Safari. It has its own
 * storage, so a session saved in Safari is not in it: the first time the icon
 * opens, nobody is signed in. Before staylogged that first open drew the
 * marketing homepage, and people read it as "Karr logged me out" every time
 * (founder, 3 Oct 2026). Inside the icon there is nothing to market, so a
 * signed-out open goes straight to sign-in, and once signed in there it stays.
 *
 * @since staylogged
 */

/** True inside an installed app (Android or iPhone home-screen icon). */
export function isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    } catch {
        return false;
    }
}

/** iPhone, iPod or iPad (iPadOS 13+ reports as "MacIntel", so ask about touch). */
export function isIOSDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /iphone|ipad|ipod/i.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** iOS version as [major, minor] from the user agent, or null where it does not say. */
export function iosVersion(): [number, number] | null {
    if (typeof navigator === 'undefined' || !isIOSDevice()) return null;
    const m = navigator.userAgent.match(/OS (\d+)[_.](\d+)/);
    return m ? [Number(m[1]), Number(m[2])] : null;
}

/** "iPad" or "iPhone": what the person is holding, for the words on screen. */
export function appleDeviceName(): 'iPad' | 'iPhone' {
    if (typeof navigator === 'undefined') return 'iPhone';
    return /ipad/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'iPad' : 'iPhone';
}

let asked = false;

/**
 * Ask the browser never to clear Karr's storage, which is where the session
 * lives. Without this a phone low on space may evict a site's storage, and the
 * session goes with it. Installed apps are normally granted it without a
 * question asked (Chrome on Android, Safari 17+), once per load. A refusal or an
 * older browser changes nothing: storage is then kept as it always was.
 */
export function keepSignInStorage(): void {
    if (asked) return;
    asked = true;
    try {
        const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined;
        if (!storage?.persist) return;
        void storage.persist().catch(() => { /* not granted: nothing to do */ });
    } catch { /* storage API missing or blocked */ }
}
